import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ExternalLink, BookOpen, Users, Calendar, Star, Flame, FileText, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import FileUpload from '../../components/admin/FileUpload';
import { getFileUrl } from '../../lib/utils';

const ResearchManagement = () => {
  const { toast } = useToast();
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    doc_number: '',
    title_ar: '',
    title_en: '',
    authors: [''],
    journal_name: '',
    doi: '',
    research_url: '',
    pdf_url: '',
    citation: '',
    indexing: '',
    quartile: '',
    impact_factor: '',
    publication_date: '',
    publication_status: 'Published',
    abstract_ar: '',
    abstract_en: '',
    keywords: [''],
    is_published: true
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchResearch();
  }, []);

  const fetchResearch = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/research/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setResearch(data);
    } catch (error) {
      console.error('Error fetching research:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const cleanedData = {
        ...formData,
        doc_number: formData.doc_number ? parseInt(formData.doc_number) : null,
        authors: formData.authors.filter(a => a && a.trim() !== ''),
        keywords: formData.keywords.filter(k => k && k.trim() !== '')
      };

      const url = editingId
        ? `${BACKEND_URL}/api/research/${editingId}`
        : `${BACKEND_URL}/api/research/`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        toast({
          title: editingId ? 'تم التحديث' : 'تم الإضافة',
          description: editingId ? 'تم تحديث البحث بنجاح' : 'تم إضافة البحث بنجاح'
        });
        fetchResearch();
        resetForm();
      }
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء حفظ البحث',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      doc_number: item.doc_number || '',
      title_ar: item.title_ar || '',
      title_en: item.title_en || '',
      authors: Array.isArray(item.authors) ? item.authors : (item.authors ? [item.authors] : ['']),
      journal_name: item.journal_name || '',
      doi: item.doi || '',
      research_url: item.research_url || '',
      pdf_url: item.pdf_url || '',
      citation: item.citation || '',
      indexing: item.indexing || '',
      quartile: item.quartile || '',
      impact_factor: item.impact_factor || '',
      publication_date: item.publication_date || '',
      publication_status: item.publication_status || 'Published',
      abstract_ar: item.abstract_ar || '',
      abstract_en: item.abstract_en || '',
      keywords: Array.isArray(item.keywords) ? item.keywords : (item.keywords ? [item.keywords] : ['']),
      is_published: item.is_published !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا البحث؟')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/research/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'تم الحذف',
          description: 'تم حذف البحث بنجاح'
        });
        fetchResearch();
      }
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء حذف البحث',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      doc_number: '',
      title_ar: '',
      title_en: '',
      authors: [''],
      journal_name: '',
      doi: '',
      research_url: '',
      pdf_url: '',
      citation: '',
      indexing: '',
      quartile: '',
      impact_factor: '',
      publication_date: '',
      publication_status: 'Published',
      abstract_ar: '',
      abstract_en: '',
      keywords: [''],
      is_published: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addAuthor = () => {
    setFormData({ ...formData, authors: [...formData.authors, ''] });
  };

  const removeAuthor = (index) => {
    const newAuthors = formData.authors.filter((_, i) => i !== index);
    setFormData({ ...formData, authors: newAuthors });
  };

  const updateAuthor = (index, value) => {
    const newAuthors = [...formData.authors];
    newAuthors[index] = value;
    setFormData({ ...formData, authors: newAuthors });
  };

  const addKeyword = () => {
    setFormData({ ...formData, keywords: [...formData.keywords, ''] });
  };

  const removeKeyword = (index) => {
    const newKeywords = formData.keywords.filter((_, i) => i !== index);
    setFormData({ ...formData, keywords: newKeywords });
  };

  const updateKeyword = (index, value) => {
    const newKeywords = [...formData.keywords];
    newKeywords[index] = value;
    setFormData({ ...formData, keywords: newKeywords });
  };

  const filteredResearch = research.filter(item => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return true;
    return (
      (item.title_ar && item.title_ar.toLowerCase().includes(q)) ||
      (item.title_en && item.title_en.toLowerCase().includes(q)) ||
      (item.journal_name && item.journal_name.toLowerCase().includes(q)) ||
      (item.doi && item.doi.toLowerCase().includes(q)) ||
      (item.quartile && item.quartile.toLowerCase().includes(q)) ||
      (Array.isArray(item.authors) && item.authors.some(a => a.toLowerCase().includes(q)))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0F388E]">
              {editingId ? 'تعديل بحث علمي' : 'إضافة بحث جديد'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              إدارة وتوثيق الأبحاث العلمية المنشورة في المجلات والدوريات الطبية
            </p>
          </div>
          <Button variant="outline" onClick={resetForm}>
            إلغاء
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Titles Card */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-[#0F388E] border-b pb-2">عناوين وبيانات البحث الأساسية</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رقم البحث في التوثيق
                  </label>
                  <input
                    type="number"
                    value={formData.doc_number}
                    onChange={(e) => setFormData({ ...formData, doc_number: e.target.value })}
                    placeholder="مثال: 1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
                
                <div className="md:col-span-9">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    عنوان البحث باللغة الإنجليزية (English Title) *
                  </label>
                  <input
                    type="text"
                    dir="ltr"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    required
                    placeholder="e.g. In Vivo Colour Stability of Composite Attachments..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-left"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان البحث باللغة العربية (إن وجد)
                </label>
                <input
                  type="text"
                  value={formData.title_ar}
                  onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                  placeholder="عنوان البحث بالعربية..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>

              {/* Authors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  أسماء الباحثين (Authors)
                </label>
                {formData.authors.map((author, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      dir="ltr"
                      value={author}
                      onChange={(e) => updateAuthor(index, e.target.value)}
                      placeholder="e.g. Al-Mogahed NM, Aldhorae K"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-left"
                    />
                    {formData.authors.length > 1 && (
                      <Button type="button" variant="outline" onClick={() => removeAuthor(index)}>
                        حذف
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addAuthor} className="mt-1">
                  + إضافة باحث
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Academic Indexing & Quality Card */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-[#0F388E] border-b pb-2">التصنيف الأكاديمي والفهرسة العالمية</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم المجلة أو الدورية
                  </label>
                  <input
                    type="text"
                    value={formData.journal_name}
                    onChange={(e) => setFormData({ ...formData, journal_name: e.target.value })}
                    placeholder="مثال: The Lancet, BMC Oral Health"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تصنيف الربع (Quartile)
                  </label>
                  <select
                    value={formData.quartile}
                    onChange={(e) => setFormData({ ...formData, quartile: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  >
                    <option value="">غير محدد</option>
                    <option value="Q1">Q1 (الربع الأول الأعلى تأثيراً)</option>
                    <option value="Q2">Q2 (الربع الثاني)</option>
                    <option value="Q3">Q3 (الربع الثالث)</option>
                    <option value="Q4">Q4 (الربع الرابع)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    معامل التأثير (Impact Factor)
                  </label>
                  <input
                    type="text"
                    value={formData.impact_factor}
                    onChange={(e) => setFormData({ ...formData, impact_factor: e.target.value })}
                    placeholder="مثال: 88.5 أو 3.1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    المعرف الرقمي للبحث (DOI)
                  </label>
                  <input
                    type="text"
                    dir="ltr"
                    value={formData.doi}
                    onChange={(e) => setFormData({ ...formData, doi: e.target.value })}
                    placeholder="10.1016/S0140-6736(24)00000-0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-left"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    قواعد الفهرسة المعتمدة (Indexing)
                  </label>
                  <input
                    type="text"
                    value={formData.indexing}
                    onChange={(e) => setFormData({ ...formData, indexing: e.target.value })}
                    placeholder="Web of Science, Scopus, PubMed"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة النشر (Status)
                  </label>
                  <select
                    value={formData.publication_status}
                    onChange={(e) => setFormData({ ...formData, publication_status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  >
                    <option value="Published">منشور (Published)</option>
                    <option value="In Press">قيد الطباعة (In Press)</option>
                    <option value="Accepted">مقبول للنشر (Accepted)</option>
                    <option value="Under Review">قيد التحكيم (Under Review)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ النشر (Publication Date)
                  </label>
                  <input
                    type="text"
                    value={formData.publication_date}
                    onChange={(e) => setFormData({ ...formData, publication_date: e.target.value })}
                    placeholder="YYYY-MM-DD أو السنة مثلاً: 2024"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رابط البحث المباشر (URL)
                  </label>
                  <input
                    type="url"
                    dir="ltr"
                    value={formData.research_url}
                    onChange={(e) => setFormData({ ...formData, research_url: e.target.value })}
                    placeholder="https://doi.org/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-left"
                  />
                </div>
              </div>

              <div>
                <FileUpload
                  label="ملف البحث (PDF) أو رابط خارجي"
                  value={formData.pdf_url}
                  onChange={(url) => setFormData({ ...formData, pdf_url: url })}
                  accept=".pdf"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  التوثيق الأكاديمي المعتمد (Chicago Citation)
                </label>
                <textarea
                  dir="ltr"
                  value={formData.citation}
                  onChange={(e) => setFormData({ ...formData, citation: e.target.value })}
                  rows={3}
                  placeholder="Author(s). 'Title of Paper.' Journal Name volume (year): pages."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-left font-mono text-xs"
                />
              </div>
            </CardContent>
          </Card>

          {/* Abstract Card */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-[#0F388E] border-b pb-2">ملخص البحث (Abstract)</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الملخص بالعربية (اختياري)
                  </label>
                  <textarea
                    value={formData.abstract_ar}
                    onChange={(e) => setFormData({ ...formData, abstract_ar: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الملخص بالإنجليزية (English Abstract)
                  </label>
                  <textarea
                    dir="ltr"
                    value={formData.abstract_en}
                    onChange={(e) => setFormData({ ...formData, abstract_en: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-left"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4 text-[#0F388E] rounded"
                />
                <label htmlFor="is_published" className="text-sm font-bold text-gray-700 cursor-pointer">
                  نشر البحث وتفعيله للعرض في الموقع
                </label>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="submit" className="bg-[#0F388E] hover:bg-[#1447b3] px-6">
              {editingId ? 'تحديث بيانات البحث' : 'إضافة البحث'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              إلغاء
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0F388E]">إدارة الأبحاث المنشورة</h1>
          <p className="text-gray-600 mt-1">
            سجل الأبحاث العلمية والإنتاج البحثي لجامعة ابن النفيس ({research.length} بحثاً مسجلاً)
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#0F388E] hover:bg-[#1447b3] shadow-md">
          <Plus className="ml-2 w-5 h-5" />
          إضافة بحث جديد
        </Button>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-xl border shadow-sm">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="ابحث بالعنوان، اسم الباحث، المجلة، DOI، أو التصنيف (Q1, Q2)..."
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F388E] text-sm"
        />
      </div>

      {filteredResearch.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">لا توجد أبحاث مطابقة للبحث</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredResearch.map((item) => {
            const hasHighIf = parseFloat(item.impact_factor) >= 5.0;
            return (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start gap-4 mb-3">
                    <div className="flex-1">
                      {/* Meta Tags */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {item.doc_number && (
                          <span className="px-2 py-0.5 rounded bg-[#0F388E] text-white text-xs font-black">
                            #{item.doc_number}
                          </span>
                        )}
                        {item.quartile && (
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            item.quartile === 'Q1'
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              : 'bg-blue-100 text-blue-800 border border-blue-300'
                          }`}>
                            تصنيف {item.quartile}
                          </span>
                        )}
                        {item.impact_factor && (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-50 text-rose-800 border border-rose-200">
                            IF: {item.impact_factor}
                          </span>
                        )}
                        {item.publication_date && (
                          <span className="text-xs text-gray-500 font-medium">
                            {item.publication_date}
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 dir="ltr" className="text-left text-base font-bold text-gray-900 mb-1">
                        {item.title_en || item.title_ar}
                      </h3>

                      {item.journal_name && (
                        <p className="text-xs font-semibold text-[#C9A040] mb-2">
                          {item.journal_name}
                        </p>
                      )}

                      {/* Authors */}
                      {Array.isArray(item.authors) && item.authors.length > 0 && (
                        <p dir="ltr" className="text-left text-xs text-gray-600 mb-2">
                          <span className="font-bold text-gray-500">Authors: </span>
                          {item.authors.join(' • ')}
                        </p>
                      )}

                      {/* Citation */}
                      {item.citation && (
                        <div dir="ltr" className="text-left text-xs text-gray-600 bg-gray-50 p-2.5 rounded-lg border font-mono mb-2">
                          {item.citation}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(item)} title="تعديل">
                        <Edit className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600 hover:bg-red-50" title="حذف">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t">
                    <div className="flex items-center gap-3">
                      {item.doi && (
                        <a 
                          href={item.doi.startsWith('http') ? item.doi : `https://doi.org/${item.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0F388E] hover:underline flex items-center gap-1 font-mono"
                        >
                          <ExternalLink className="w-3 h-3" />
                          DOI: {item.doi}
                        </a>
                      )}
                      {item.pdf_url && (
                        <a 
                          href={getFileUrl(item.pdf_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-700 hover:underline flex items-center gap-1 font-bold"
                        >
                          <Download className="w-3 h-3" />
                          ملف PDF
                        </a>
                      )}
                    </div>

                    {!item.is_published && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded font-medium">
                        مسودة غير منشورة
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ResearchManagement;
